// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import { Button, buttonTypes } from 'moods-react/Button';
import { icons } from 'moods-react/Icons';
import Search from '../../../common/search/SearchComponent';
import Results from '../../results/ResultsComponent';
import commonMessages from '../../../../data/common';
import productMessages from '../../../../data/products';
import { getDefaultProductSettings } from '../../reducers/settings/SettingsHelpers';
import { envUrl } from '../../../services/environment/EnvironmentService';
import { getProductRouteById } from '../../../services/products-route/ProductsRouteService';
import AnalyticsClient from '../../../../clients/AnalyticsClient';
import productEvents from '../../../../analytics/events/products';

import './SearchBar.scss';

type Props = {
  productTypeId: string,
  productSort: string,
  totalItemCount: number,
  onOpenFilters?: Function,
  searchTerm?: string,
  isLoading?: boolean,
  hasBulkSelection?: boolean,
  template: string,
  productSize: string,
  actions: {
    productSelectAllAction: Function,
  },
};

type Context = {
  intl: Object,
};
/**
 * Displays Search Bar for filtering products
 */
class SearchBarComponent extends React.Component<Props, Context> {
  stopPropagation(e: { stopPropagation: Function }): void {
    e.stopPropagation();
  }

  shouldComponentUpdate(newProps: Props): boolean {
    return (
      newProps.searchTerm !== this.props.searchTerm ||
      newProps.isLoading !== this.props.isLoading ||
      newProps.totalItemCount !== this.props.totalItemCount ||
      newProps.productSize !== this.props.productSize ||
      newProps.template !== this.props.template ||
      newProps.hasBulkSelection !== this.props.hasBulkSelection
    );
  }

  requestCurrentProducts(searchTerm: string): void {
    const { template, productSize, productSort } = this.props;
    const filterString = `?template=${template}&productSize=${productSize}&productSort=${productSort}&searchTerm=${searchTerm}`;
    const route = getProductRouteById(this.props.productTypeId);
    this.props.history.push({
      pathname: envUrl(`/products/${route}`),
      search: filterString,
    });
  }

  enterSearch = (text: string): void => {
    this.requestCurrentProducts(text);
    AnalyticsClient.trackEvent(productEvents.productSearch());
  };

  selectAll = (): void => {
    this.props.actions.productSelectAllAction();
    AnalyticsClient.trackEvent(productEvents.productSelectAll());
  };

  searchBarCTAStatus(): { hasFilters: boolean, isEnabledFilter: boolean, isEnabledSelect: boolean } {
    const { totalItemCount, productSize, template } = this.props;
    const defaults = getDefaultProductSettings();
    const hasFilters = productSize !== defaults.productSize || template !== defaults.template;

    return {
      hasFilters,
      isEnabledFilter: hasFilters || totalItemCount > 0,
      isEnabledSelect: totalItemCount > 0,
    };
  }

  render(): void {
    const text = this.context.intl.formatMessage;
    const ctaStatus = this.searchBarCTAStatus();

    return (
      <div className="search-bar-component">
        <div className="search-bar-component__search">
          <Search
            placeholder={this.props.searchTerm ? '' : text(commonMessages.searchBarPlaceholder)} // Search
            value={this.props.searchTerm}
            onSearchCallback={this.enterSearch}
          />
        </div>

        <div className="search-bar-component__actions">
          <Results totalResults={this.props.totalItemCount} isLoading={this.props.isLoading} />
          <div className="search-bar-component__filter" onClick={this.stopPropagation}>
            <Button
              type={buttonTypes.GHOST_DARK}
              disabled={!ctaStatus.isEnabledFilter}
              onClick={this.props.onOpenFilters}
              icon={ctaStatus.hasFilters ? icons.UI_TICK_CIRCLE : null}
            >
              {text(productMessages.filterAndSortButton)}
            </Button>
          </div>
          {this.props.hasBulkSelection && (
            <Button
              data-selectall
              type={buttonTypes.SECONDARY}
              disabled={!ctaStatus.isEnabledSelect}
              onClick={this.selectAll}
            >
              {text(productMessages.selectAllProductsButton)}
            </Button>
          )}
        </div>
      </div>
    );
  }
}

SearchBarComponent.displayName = 'SearchBar';

SearchBarComponent.defaultProps = {
  hasBulkSelection: true,
};

SearchBarComponent.propTypes = {
  productTypeId: PropTypes.string,
  productSort: PropTypes.string.isRequired,
  totalItemCount: PropTypes.number.isRequired,
  onOpenFilters: PropTypes.func,
  searchTerm: PropTypes.string,
  isLoading: PropTypes.bool,
  hasBulkSelection: PropTypes.bool,
  template: PropTypes.string.isRequired,
  productSize: PropTypes.string.isRequired,
  actions: PropTypes.shape({
    productSelectAllAction: PropTypes.func.isRequired,
  }).isRequired,
};

SearchBarComponent.defaultProps = {
  onOpenFilters() {},
  onSelectAll() {},
  isLoading: false,
};

SearchBarComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default SearchBarComponent;
